// Shared time zone presets and helpers.
//
// Used by the Profile page and the sign-up form so the list and formatting
// helpers stay in one place and can't drift between the two screens. The
// presets focus on North America (the regions we serve); the "Custom..."
// entry in each screen still accepts any IANA zone, and the backend
// validates whatever is saved.

// Grouped presets for rendering <optgroup> menus. Each zone is { value, label }
// where value is an IANA name and label is user-facing (plain hyphen only - no
// en/em dashes per repo convention).
export const NORTH_AMERICA_TIME_ZONE_GROUPS = [
  {
    region: "US & Canada",
    zones: [
      { value: "America/New_York", label: "Eastern Time - US & Canada (America/New_York)" },
      { value: "America/Chicago", label: "Central Time - US & Canada (America/Chicago)" },
      { value: "America/Denver", label: "Mountain Time - US & Canada (America/Denver)" },
      { value: "America/Phoenix", label: "Arizona - no DST (America/Phoenix)" },
      { value: "America/Los_Angeles", label: "Pacific Time - US & Canada (America/Los_Angeles)" },
      { value: "America/Anchorage", label: "Alaska Time (America/Anchorage)" },
      { value: "America/Adak", label: "Aleutian Time (America/Adak)" },
      { value: "Pacific/Honolulu", label: "Hawaii Time (Pacific/Honolulu)" },
      { value: "America/Halifax", label: "Atlantic Time - Canada (America/Halifax)" },
      { value: "America/St_Johns", label: "Newfoundland Time (America/St_Johns)" },
    ],
  },
  {
    region: "Mexico",
    zones: [
      { value: "America/Mexico_City", label: "Central Time - Mexico (America/Mexico_City)" },
      { value: "America/Chihuahua", label: "Mountain Time - Mexico (America/Chihuahua)" },
      { value: "America/Mazatlan", label: "Pacific Time - Mexico (America/Mazatlan)" },
      { value: "America/Tijuana", label: "Northwest Pacific - Mexico (America/Tijuana)" },
      { value: "America/Cancun", label: "Southeast - Mexico (America/Cancun)" },
    ],
  },
  {
    region: "Caribbean & Atlantic",
    zones: [
      { value: "America/Puerto_Rico", label: "Atlantic Time - Puerto Rico & US Virgin Islands (America/Puerto_Rico)" },
      { value: "America/Santo_Domingo", label: "Dominican Republic (America/Santo_Domingo)" },
      { value: "America/Havana", label: "Cuba (America/Havana)" },
      { value: "America/Jamaica", label: "Jamaica (America/Jamaica)" },
      { value: "America/Nassau", label: "Bahamas (America/Nassau)" },
    ],
  },
  {
    region: "Other",
    zones: [{ value: "UTC", label: "UTC" }],
  },
];

// Flat list of every preset value - used to decide whether a saved zone is a
// known preset or should drop into the "Custom..." field.
export const TIME_ZONE_VALUES = NORTH_AMERICA_TIME_ZONE_GROUPS.flatMap((group) =>
  group.zones.map((zone) => zone.value)
);

export function getBrowserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

export function isValidTimeZone(tz) {
  if (!tz) return false;
  try {
    Intl.DateTimeFormat(undefined, { timeZone: tz }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

export function formatNowForTimeZone(tz) {
  const zone = tz || "UTC";
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone: zone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date());
  } catch {
    return new Date().toLocaleString();
  }
}

export function formatDateForTimeZone(value, tz, options = {}) {
  if (!value) return "";
  const zone = tz || "UTC";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone: zone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      ...options,
    }).format(date);
  } catch {
    return date.toLocaleString();
  }
}
